import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  type AdminApproval,
  AdminApprovalDecision,
  AdminApprovalType,
  type Role,
  adminapprovalReviewAdminApprovalMutation,
} from '@column/api';
import { Button, Fade, Icon, Input } from '@column/column-ui-kit';
import { DialogFooter, Label } from '@column/ui';

import { CopyRow } from '~/components/CopyRow';
import { LogoLoading } from '~/elements';
import { useApprovalTargets } from '~/lib/approvals/hooks/useApprovalTargets';
import { useDecisionAnimation } from '~/lib/approvals/hooks/useDecisionAnimation';
import { useDelayedReveal } from '~/lib/approvals/hooks/useDelayedReveal';
import { useReviewKeyboardShortcuts } from '~/lib/approvals/hooks/useReviewKeyboardShortcuts';
import { useReviewQueue } from '~/lib/approvals/hooks/useReviewQueue';
import { useReviewTransition } from '~/lib/approvals/hooks/useReviewTransition';
import { invalidateApprovalSubjects, rolesQueryOptions, usersQueryOptions } from '~/lib/approvals/queries';
import { VerticalScrollFade } from '~/lib/components/VerticalScrollFade';
import { useNavigate } from '~/lib/navigation';
import { LoginAuthMethod } from '~/lib/session/LoginAuthMethod';
import { DashboardUserRedacted } from '~/repositories';
import { MFAStatus } from '~/repositories/UserRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Card, Text } from '~/styles';
import { formatDateTime } from '~/util';

import { AnimatedButton } from './AnimatedButton';
import { ApprovalDetails, getApprovalHeader, getApprovalRolePath } from './ApprovalDetails';
import {
  AnimatedHeight,
  ApproveButton,
  DIALOG_WIDEN_MS,
  RejectButton,
  ReviewDialog,
  ReviewDialogHeader,
  ReviewItemHeader,
} from './ReviewDialog';

const MAX_COMMENT_LENGTH = 1023;
const REVIEW_REQUIRES_MFA_MESSAGE = 'Enable two-factor authentication in your profile to review changes.';
const NO_USERS: DashboardUserRedacted[] = [];
const NO_ROLES: KeysCamelCaseDeep<Role>[] = [];

const syncWithLatest = (previous: AdminApproval[], latest: AdminApproval[]) => {
  const byId = new Map(latest.map((approval) => [approval.id, approval]));
  const next = previous.flatMap((approval) => byId.get(approval.id) ?? []);
  return next.length === previous.length && next.every((approval, i) => approval === previous[i]) ? previous : next;
};

interface Props {
  approvals: AdminApproval[];
  startIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onReviewed?: () => void;
  hideCounter?: boolean;
}

export const AdminApprovalReviewDialog: React.FC<Props> = ({
  approvals: approvalsProp,
  startIndex,
  isOpen,
  onClose,
  onReviewed,
  hideCounter,
}) => {
  const { currentUser, currentPlatform, mfaStatus, loginAuthMethod } = useSessionStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { addDangerNotification } = useNotificationStore();
  const {
    items: approvals,
    setItems: setApprovals,
    currentIndex,
    setCurrentIndex,
    removeById,
  } = useReviewQueue<AdminApproval>();
  const { showContent, setShowContent, slideDirection, slide, cancel: cancelTransition } = useReviewTransition();
  const { approveAnimation, rejectAnimation, play: playAnimation, reset: resetAnimation } = useDecisionAnimation();
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState<AdminApprovalDecision>();
  const commentFocused = useRef(false);

  const wasOpen = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      wasOpen.current = false;
      cancelTransition();
      setShowContent(true);
      return;
    }
    if (!wasOpen.current) {
      wasOpen.current = true;
      setApprovals(approvalsProp);
      setCurrentIndex(startIndex);
      setComment('');
      return;
    }
    const next = syncWithLatest(approvals, approvalsProp);
    if (next === approvals) return;
    const currentId = approvals[currentIndex]?.id;
    setApprovals(next);
    const index = next.findIndex((approval) => approval.id === currentId);
    setCurrentIndex(index >= 0 ? index : currentIndex);
  }, [
    approvals,
    approvalsProp,
    cancelTransition,
    currentIndex,
    isOpen,
    setApprovals,
    setCurrentIndex,
    setShowContent,
    startIndex,
  ]);

  const needsRoleContext = approvalsProp.some(
    (approval) => approval.type !== AdminApprovalType.PLATFORM_UPDATE_REQUIRE_USER_CHANGE_APPROVAL
  );
  const { data: usersResponse, isLoading: isUsersLoading } = useQuery({
    ...usersQueryOptions(currentPlatform?.id),
    enabled: isOpen && !!currentPlatform?.id && needsRoleContext,
    staleTime: Infinity,
  });
  const { data: rolesResponse, isLoading: isRolesLoading } = useQuery({
    ...rolesQueryOptions(),
    enabled: (isOpen || approvalsProp.length > 0) && needsRoleContext,
    staleTime: Infinity,
  });
  const users = usersResponse?.dashboardUsers ?? NO_USERS;
  const roles = rolesResponse?.roles ?? NO_ROLES;
  const {
    targets,
    accountOwners,
    isLoading: isTargetsLoading,
  } = useApprovalTargets(approvalsProp, roles, approvalsProp.length > 0);
  const isBodyLoading = isUsersLoading || isRolesLoading || isTargetsLoading;
  const { isVisible, isHoldingSpinner } = useDelayedReveal(isOpen, isBodyLoading);
  const isShowingSpinner = isBodyLoading || isHoldingSpinner;

  const reviewMutation = useMutation(adminapprovalReviewAdminApprovalMutation());

  const current = approvals[currentIndex];
  const emptyState = approvals.length === 0;
  const isRequester = current?.requested_by.id === currentUser?.id;
  const hasMfaEnabled =
    mfaStatus === MFAStatus.VERIFIED ||
    mfaStatus === MFAStatus.VERIFIED_LEGACY ||
    loginAuthMethod === LoginAuthMethod.PASSKEY;
  const canApprove = !!current?.can_review && hasMfaEnabled;
  const canReject = canApprove || (!!current && isRequester);
  const rolePath = current && getApprovalRolePath(current);
  const wideRef = useRef(false);
  if (!isOpen || emptyState) wideRef.current = false;
  else if (showContent && current) {
    wideRef.current =
      current.type === AdminApprovalType.USER_ROLE_ASSIGN || current.type === AdminApprovalType.ROLE_UPDATE;
  }
  const isWide = wideRef.current;

  const navigateTo = useCallback(
    (path: string) => {
      onClose();
      navigate(path);
    },
    [navigate, onClose]
  );

  const goTo = useCallback(
    (index: number, direction: 'up' | 'down') => {
      commentFocused.current = false;
      slide(direction, () => {
        setComment('');
        setCurrentIndex(index);
      });
    },
    [setCurrentIndex, slide]
  );
  const goPrevious = useCallback(() => {
    if (currentIndex > 0) goTo(currentIndex - 1, 'down');
  }, [currentIndex, goTo]);
  const goNext = useCallback(() => {
    if (currentIndex < approvals.length - 1) goTo(currentIndex + 1, 'up');
  }, [approvals.length, currentIndex, goTo]);

  const handleSubmit = useCallback(
    async (decision: AdminApprovalDecision) => {
      if (!current || isSubmitting) return;
      const isApproval = decision === AdminApprovalDecision.APPROVED;
      if (isApproval ? !canApprove : !canReject) return;
      const approvalId = current.id;
      setIsSubmitting(decision);
      try {
        await reviewMutation.mutateAsync({
          path: { admin_approval_id: approvalId },
          body: { decision, comment: comment.trim() || undefined },
        });
        await playAnimation(isApproval ? 'approve' : 'reject');
      } catch (error) {
        addDangerNotification({ content: (error as { message?: string })?.message || 'Failed to review change' });
        return;
      } finally {
        setIsSubmitting(undefined);
        resetAnimation();
      }
      invalidateApprovalSubjects(queryClient);
      onReviewed?.();
      removeById(approvalId);
      setComment('');
    },
    [
      addDangerNotification,
      canApprove,
      canReject,
      comment,
      current,
      isSubmitting,
      onReviewed,
      playAnimation,
      queryClient,
      removeById,
      resetAnimation,
      reviewMutation,
    ]
  );
  const approve = useCallback(() => handleSubmit(AdminApprovalDecision.APPROVED), [handleSubmit]);
  const reject = useCallback(() => handleSubmit(AdminApprovalDecision.REJECTED), [handleSubmit]);

  useReviewKeyboardShortcuts({
    enabled: isVisible,
    isBlocked: !!isSubmitting,
    pauseNavigationRef: commentFocused,
    onApprove: approve,
    onReject: reject,
    onNext: goNext,
    onPrevious: goPrevious,
  });

  return (
    <ReviewDialog
      isOpen={isVisible}
      onClose={onClose}
      showCloseButton={false}
      isWide={isWide}
      className="sm:max-w-[640px]"
    >
      <ReviewDialogHeader
        title="Review Change"
        description="Approve or reject changes pending approval."
        showCounter={!emptyState && !hideCounter}
        slideDirection={slideDirection}
        current={currentIndex + 1}
        total={approvals.length}
        onPrevious={goPrevious}
        onNext={goNext}
      />

      <AnimatedHeight freeze={!showContent} durationMs={DIALOG_WIDEN_MS}>
        {isShowingSpinner && !emptyState ? (
          <div className="relative min-h-[200px]">
            <LogoLoading className="!top-1/2" />
          </div>
        ) : emptyState || !current ? (
          <div className="flex min-h-[200px] flex-col items-center justify-center gap-2 text-center">
            <Text size={18} weight="medium" color="foreground">
              All caught up
            </Text>
            <Text size={14} color="light">
              There are no more changes to review
            </Text>
          </div>
        ) : (
          <Fade
            key={currentIndex}
            show={showContent}
            distance={16}
            direction={slideDirection === 'up' ? 'right' : 'left'}
          >
            <VerticalScrollFade
              className="-mx-6"
              scrollClassName="max-h-[calc(100dvh-20rem)] px-6 py-1"
              contentClassName="grid gap-6"
            >
              <Card>
                <ReviewItemHeader
                  {...getApprovalHeader(current, users, roles)}
                  action={
                    rolePath && (
                      <Button
                        className="!p-0"
                        icon={<Icon.ChevronRightLarge />}
                        iconRight
                        variant="subtle"
                        onClick={() => navigateTo(rolePath)}
                      >
                        View
                      </Button>
                    )
                  }
                />
                <ApprovalDetails
                  approval={current}
                  roles={roles}
                  targets={targets}
                  accountOwners={accountOwners}
                  onNavigate={navigateTo}
                />
                <CopyRow label="Requested By" value={isRequester ? 'You' : current.requested_by.name} />
                <CopyRow label="Requested At" value={formatDateTime(current.created_at, { timeZoneFormat: 'local' })} />
              </Card>
              {!canReject && (
                <Text size={14} color="light">
                  {current.can_review ? REVIEW_REQUIRES_MFA_MESSAGE : 'You are not eligible to review this change.'}
                </Text>
              )}
            </VerticalScrollFade>
          </Fade>
        )}
      </AnimatedHeight>

      {!emptyState && current && canReject && !isShowingSpinner && (
        <div className="grid gap-2">
          <Label>Comment</Label>
          <Input
            value={comment}
            isDisabled={!!isSubmitting}
            placeholder="Add a comment..."
            onChange={(value: string) => setComment(value.slice(0, MAX_COMMENT_LENGTH))}
            onFocus={() => (commentFocused.current = true)}
            onBlur={() => (commentFocused.current = false)}
          />
        </div>
      )}

      <DialogFooter className="flex-row items-center justify-between sm:justify-between">
        <AnimatedButton onClick={onClose} variant="secondary">
          Close
        </AnimatedButton>
        {!emptyState && (
          <div className="flex flex-row items-center justify-end gap-2">
            {canReject && (
              <RejectButton
                onClick={reject}
                isLoading={isSubmitting === AdminApprovalDecision.REJECTED && !rejectAnimation}
                isDisabled={isSubmitting === AdminApprovalDecision.APPROVED}
                animationState={rejectAnimation}
              >
                {canApprove ? 'Reject' : 'Cancel'}
              </RejectButton>
            )}
            {canApprove && (
              <ApproveButton
                onClick={approve}
                isLoading={isSubmitting === AdminApprovalDecision.APPROVED && !approveAnimation}
                isDisabled={isSubmitting === AdminApprovalDecision.REJECTED}
                animationState={approveAnimation}
              >
                Approve
              </ApproveButton>
            )}
          </div>
        )}
      </DialogFooter>
    </ReviewDialog>
  );
};
